/**
 * Custom hook for managing pipeline configuration
 * Provides CRUD operations for pipeline configuration via API
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';

// Get API base URL (matches ApiService configuration)
const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    return `${apiUrl}/api`;
  } else if (process.env.NODE_ENV === "production") {
    return "https://api.roboworks.com/api";
  } else {
    return "http://localhost:8000/api";
  }
};

export interface AgentConfig {
  id: string;
  name: string;
  class_name: string;
  module_path: string;
  description: string;
  icon: string;
  category: string;
  timeout: number;
  required: boolean;
  dependencies: string[];
  metadata: Record<string, any>;
}

export interface PipelineConfig {
  version: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  agents: AgentConfig[];
}

export interface PipelineConfigResponse {
  config: PipelineConfig;
  execution_order: string[];
  is_active: boolean;
  last_modified?: string;
}

export interface AgentManifest {
  id: string;
  name: string;
  description: string;
  version: string;
  category: string;
  capabilities: string[];
  dependencies: Array<{
    agent_id: string;
    optional: boolean;
  }>;
  inputs: Record<string, any>;
  outputs: Record<string, any>;
  metadata: Record<string, any>;
}

export interface AvailableAgent {
  id: string;
  manifest: AgentManifest;
  is_registered: boolean;
  is_available: boolean;
}

export interface PipelineHealth {
  healthy: boolean;
  components: {
    config_service: {
      healthy: boolean;
      active_config: boolean;
      agent_count: number;
    };
    agent_registry: {
      healthy: boolean;
      discovered_agents: number;
      registered_agents: number;
    };
  };
  timestamp: string;
}

interface UsePipelineConfigReturn {
  // State
  config: PipelineConfigResponse | null;
  availableAgents: AvailableAgent[];
  health: PipelineHealth | null;
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchConfig: () => Promise<void>;
  updateConfig: (config: PipelineConfig, saveToFile?: boolean) => Promise<boolean>;
  fetchAvailableAgents: () => Promise<void>;
  reloadPipeline: () => Promise<boolean>;
  fetchHealth: () => Promise<void>;
  
  // Utilities
  getAgentById: (id: string) => AgentConfig | undefined;
  getExecutionOrder: () => string[];
  isHealthy: () => boolean;
}

export const usePipelineConfig = (): UsePipelineConfigReturn => {
  const { token } = useAuth();
  const [config, setConfig] = useState<PipelineConfigResponse | null>(null);
  const [availableAgents, setAvailableAgents] = useState<AvailableAgent[]>([]);
  const [health, setHealth] = useState<PipelineHealth | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiCall = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/pipeline${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }, [token]);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiCall('/config');
      setConfig(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch pipeline config';
      setError(errorMessage);
      console.error('Error fetching pipeline config:', err);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const updateConfig = useCallback(async (
    newConfig: PipelineConfig, 
    saveToFile: boolean = true
  ): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await apiCall('/config', {
        method: 'PUT',
        body: JSON.stringify({
          config: newConfig,
          save_to_file: saveToFile,
        }),
      });
      
      setConfig(data);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update pipeline config';
      setError(errorMessage);
      console.error('Error updating pipeline config:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const fetchAvailableAgents = useCallback(async () => {
    try {
      setError(null);
      const data = await apiCall('/agents');
      setAvailableAgents(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch available agents';
      setError(errorMessage);
      console.error('Error fetching available agents:', err);
    }
  }, [apiCall]);

  const reloadPipeline = useCallback(async (): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await apiCall('/reload', {
        method: 'POST',
      });
      
      if (data.success) {
        // Refresh config after successful reload
        await fetchConfig();
        await fetchAvailableAgents();
        return true;
      } else {
        setError(data.message || 'Pipeline reload failed');
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reload pipeline';
      setError(errorMessage);
      console.error('Error reloading pipeline:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [apiCall, fetchConfig, fetchAvailableAgents]);

  const fetchHealth = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const data = await fetch(`${baseUrl}/pipeline/health`).then(res => res.json());
      setHealth(data);
    } catch (err) {
      console.error('Error fetching pipeline health:', err);
      setHealth(null);
    }
  }, []);

  // Utility functions
  const getAgentById = useCallback((id: string): AgentConfig | undefined => {
    return config?.config.agents.find(agent => agent.id === id);
  }, [config]);

  const getExecutionOrder = useCallback((): string[] => {
    return config?.execution_order || [];
  }, [config]);

  const isHealthy = useCallback((): boolean => {
    return health?.healthy === true;
  }, [health]);

  // Auto-fetch on mount and token change
  useEffect(() => {
    if (token) {
      fetchConfig();
      fetchAvailableAgents();
    }
  }, [token, fetchConfig, fetchAvailableAgents]);

  // Auto-fetch health periodically
  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [fetchHealth]);

  return {
    // State
    config,
    availableAgents,
    health,
    loading,
    error,
    
    // Actions
    fetchConfig,
    updateConfig,
    fetchAvailableAgents,
    reloadPipeline,
    fetchHealth,
    
    // Utilities
    getAgentById,
    getExecutionOrder,
    isHealthy,
  };
};
